<template>
  <nav class="side-nav" :class="{ collapsed }" style="user-select: none;">
    <!-- Brand + collapse toggle -->
    <div class="nav-top">
      <router-link to="/" class="brand-link" aria-label="Duallo home">
        <img :src="organizationLogoUrl" alt="Duallo" class="brand-logo" @error="onLogoError" />
        <img :src="dualloMark" alt="Duallo" class="brand-mark" />
      </router-link>
      <button type="button" class="collapse-btn" :title="collapsed ? 'Expand menu' : 'Collapse menu'"
        :aria-label="collapsed ? 'Expand menu' : 'Collapse menu'" @click="$emit('toggle-collapse')">
        <v-icon size="18">{{ collapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
      </button>
    </div>

    <!-- Primary navigation -->
    <div class="nav-items">
      <v-tooltip location="right" :disabled="!collapsed" text="Assets">
        <template #activator="{ props: tip }">
          <button v-bind="tip" type="button" class="nav-item"
            :class="{ active: isActive(['Assets', 'AssetLanding', 'AssetViewer']) }" @click="go('Assets')">
            <v-icon size="19">mdi-cube-outline</v-icon>
            <span class="nav-label">Assets</span>
          </button>
        </template>
      </v-tooltip>

      <v-tooltip v-if="can(Permissions.AnomalyView)" location="right" :disabled="!collapsed" text="Findings">
        <template #activator="{ props: tip }">
          <button v-bind="tip" type="button" class="nav-item"
            :class="{ active: isActive(['AnomalyManagementOrg', 'AnomalyManagement']) }" @click="go('AnomalyManagementOrg')">
            <v-icon size="19">mdi-alert-outline</v-icon>
            <span class="nav-label">Findings</span>
            <span v-if="breakdown.pending > 0" class="nav-badge">{{ breakdown.pending }}</span>
            <span v-if="breakdown.pending > 0" class="nav-dot" aria-hidden="true"></span>
          </button>
        </template>
      </v-tooltip>

      <v-tooltip v-if="canOrgAdmin" location="right" :disabled="!collapsed" text="Admin">
        <template #activator="{ props: tip }">
          <button v-bind="tip" type="button" class="nav-item" :class="{ active: isActive(['Admin']) }"
            @click="go('Admin')">
            <v-icon size="19">mdi-account-cog-outline</v-icon>
            <span class="nav-label">Admin</span>
          </button>
        </template>
      </v-tooltip>

      <v-tooltip v-if="canDualloAdmin" location="right" :disabled="!collapsed" text="Duallo admin">
        <template #activator="{ props: tip }">
          <button v-bind="tip" type="button" class="nav-item" :class="{ active: isActive(['DualloAdmin']) }"
            @click="go('DualloAdmin')">
            <v-icon size="19">mdi-shield-crown-outline</v-icon>
            <span class="nav-label">Duallo admin</span>
          </button>
        </template>
      </v-tooltip>
    </div>

    <div class="nav-spacer"></div>

    <!-- Active uploads — only rendered while something is uploading/queued/failed. -->
    <div v-if="uploads.length && !collapsed" class="upload-card" :class="{ pulsing }">
      <div class="upload-head">
        <v-icon size="15" color="#64ffda">mdi-cloud-upload-outline</v-icon>
        <span class="upload-head-title">Uploads</span>
        <span v-if="activeUploadCount" class="upload-head-count">{{ activeUploadCount }}</span>
      </div>
      <div class="upload-list">
        <div v-for="u in uploads" :key="u.id" class="upload-row" :class="'is-' + u.status">
          <div class="upload-row-top">
            <span class="upload-name" :title="u.scanName">{{ u.scanName }}</span>
            <span class="upload-state">
              <template v-if="u.status === 'uploading'">{{ u.progress }}%</template>
              <span v-else-if="u.status === 'queued'" class="upload-queued">Queued</span>
              <v-icon v-else-if="u.status === 'success'" size="15" color="#4ade80">mdi-check-circle</v-icon>
              <button v-else type="button" class="upload-retry" title="Retry upload" @click="retryUpload(u.id)">
                <v-icon size="14">mdi-refresh</v-icon>
              </button>
            </span>
          </div>
          <div class="upload-bar">
            <div class="upload-bar-fill" :class="'is-' + u.status" :style="{ width: u.progress + '%' }"></div>
          </div>
          <div v-if="u.status === 'error'" class="upload-err">
            <span class="upload-err-text" :title="u.error">{{ u.error }}</span>
            <button type="button" class="upload-dismiss" @click="removeUpload(u.id)">Dismiss</button>
          </div>
        </div>
      </div>
    </div>
    <!-- Collapsed rail: compact uploading indicator. -->
    <div v-else-if="uploads.length && collapsed" class="upload-rail" :class="{ pulsing }" :title="`${activeUploadCount} uploading`">
      <v-icon size="19" color="#64ffda">mdi-cloud-upload-outline</v-icon>
      <span v-if="activeUploadCount" class="upload-rail-badge">{{ activeUploadCount }}</span>
    </div>

    <!-- Org-wide anomaly summary -->
    <div v-if="can(Permissions.AnomalyView) && !collapsed" class="anomaly-card">
      <template v-if="breakdownLoading">
        <div class="anomaly-head"><span class="sk sk-total"></span></div>
        <div class="sk sk-bar"></div>
        <div class="anomaly-legend">
          <span class="sk sk-legend-line"></span>
          <span class="sk sk-legend-line"></span>
          <span class="sk sk-legend-line"></span>
        </div>
      </template>
      <template v-else>
        <div class="anomaly-head">
          <span class="anomaly-total">{{ formatNumber(breakdown.total) }}</span>
          <span class="anomaly-caption">anomalies</span>
        </div>
        <div class="anomaly-bar" aria-hidden="true">
          <span class="seg seg-accepted" :style="{ width: pct(breakdown.accepted) }"></span>
          <span class="seg seg-pending" :style="{ width: pct(breakdown.pending) }"></span>
          <span class="seg seg-rejected" :style="{ width: pct(breakdown.rejected) }"></span>
        </div>
        <div class="anomaly-legend">
          <span class="legend"><span class="dot dot-accepted"></span>{{ formatNumber(breakdown.accepted) }} accepted<span class="legend-pct">{{ pctLabel(breakdown.accepted) }}</span></span>
          <span class="legend"><span class="dot dot-pending"></span>{{ formatNumber(breakdown.pending) }} pending<span class="legend-pct">{{ pctLabel(breakdown.pending) }}</span></span>
          <span class="legend"><span class="dot dot-rejected"></span>{{ formatNumber(breakdown.rejected) }} rejected<span class="legend-pct">{{ pctLabel(breakdown.rejected) }}</span></span>
        </div>
      </template>
    </div>

    <!-- Subscription / plan card (hidden in the collapsed rail) -->
    <div v-if="!collapsed && hasSubscription && planLoading" class="plan-card">
      <div class="plan-head"><span class="sk sk-plan-name"></span><span class="sk sk-plan-status"></span></div>
      <div class="sk sk-plan-bar"></div>
      <div class="plan-meta"><span class="sk sk-plan-meta"></span></div>
    </div>
    <div v-else-if="!collapsed && hasSubscription && plan" class="plan-card">
      <div class="plan-head">
        <span class="plan-name">{{ plan.name }}</span>
        <span class="plan-status"><span class="status-dot"></span>Active</span>
      </div>
      <v-progress-linear :model-value="usagePct" :color="usageColor" height="4" rounded bg-color="#101820"
        class="plan-bar" />
      <div class="plan-meta">
        <span>{{ formatNumber(plan.usedMinutes) }} / {{ formatNumber(plan.monthlyMinutes) }} mins</span>
        <span v-if="renewalLabel">· renews {{ renewalLabel }}</span>
      </div>
    </div>

    <!-- User -->
    <v-menu location="top end" offset="8">
      <template #activator="{ props: menuProps }">
        <button type="button" class="user-row" v-bind="menuProps" :title="collapsed ? displayName : null">
          <span class="user-avatar">{{ initials }}</span>
          <span class="user-text">
            <span class="user-name">{{ displayName }}</span>
            <span class="user-role">{{ roleLabel }}</span>
          </span>
          <v-icon class="user-caret" size="18" color="#8aa0aa">mdi-dots-horizontal</v-icon>
        </button>
      </template>
      <v-list class="user-menu" density="compact">
        <v-list-item prepend-icon="mdi-account-circle-outline" title="Account" @click="go('Profile')" />
        <v-list-item prepend-icon="mdi-help-circle-outline" title="Contact support" @click="openHelp" />
        <v-list-item prepend-icon="mdi-logout" title="Log out" @click="requestLogout" />
      </v-list>
    </v-menu>

    <v-dialog v-model="confirmLogoutDialog" max-width="420">
      <v-card class="logout-dialog">
        <div class="logout-icon"><v-icon size="26" color="#f87171">mdi-logout</v-icon></div>
        <h2 class="logout-title">Confirm Logout</h2>
        <p class="logout-text">Are you sure you want to log out?</p>
        <div class="logout-actions">
          <button type="button" class="ghost-btn" @click="confirmLogoutDialog = false">Cancel</button>
          <button type="button" class="danger-btn" @click="confirmLogout">Logout</button>
        </div>
      </v-card>
    </v-dialog>
  </nav>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter, useRoute } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import { useOrganizationStore } from '@/store/organizationStore';
import { useAssetStore } from '@/store/assetStore';
import { useAuthStore } from '@/store/authStore';
import { useUploadStore } from '@/store/uploadStore';
import { setUnauthorizedHandler } from '@/clients/ClientBase';
import OrganizationService from '@/services/OrganizationService';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';
import { useAnomalyBreakdown } from '@/composable/useAnomalyBreakdown';

export default {
  name: 'SideNav',
  props: {
    collapsed: { type: Boolean, default: false },
  },
  emits: ['toggle-collapse'],
  setup() {
    const auth0 = useAuth0();
    const router = useRouter();
    const route = useRoute();
    const { can } = usePermissions();

    const organizationStore = useOrganizationStore();
    const assetStore = useAssetStore();
    const authStore = useAuthStore();

    // Background uploads (scan videos) — surfaced in the sidebar while active.
    const uploadStore = useUploadStore();
    const { uploads } = storeToRefs(uploadStore);
    const activeUploadCount = computed(() => uploadStore.active.length);
    const retryUpload = (id) => uploadStore.retry(id);
    const removeUpload = (id) => uploadStore.remove(id);
    // Flash the widget when a wizard hands an upload off (so the user sees where it went).
    const pulsing = ref(false);
    let pulseTimer = null;
    watch(() => uploadStore.pulseToken, () => {
      pulsing.value = true;
      clearTimeout(pulseTimer);
      pulseTimer = setTimeout(() => { pulsing.value = false; }, 1600);
    });

    const organizationLogoUrl = ref(organizationStore.organizationLogoUrl);
    const dualloMark = require('@/assets/duallo-mark.png');   // square mark for the collapsed rail
    const onLogoError = (e) => { e.target.src = require('@/assets/duallo-logo-white.png'); };

    // --- Subscription (compact plan card) ---
    const hasSubscription = ref(false);
    const plan = ref(null);
    const usagePct = ref(0);
    const renewalLabel = ref(null);
    const planLoading = ref(true);

    // Org-wide anomaly breakdown — shared with the mobile tab bar via the composable.
    const { breakdown, loading: breakdownLoading, load: loadAnomalyBreakdown } = useAnomalyBreakdown();

    const usageColor = computed(() => {
      if (usagePct.value >= 90) return '#ef4444';
      if (usagePct.value >= 70) return '#f59e0b';
      return '#3b82f6';
    });

    const pct = (value) => {
      const total = breakdown.value.total || 0;
      if (!total) return '0%';
      return `${(Number(value || 0) / total) * 100}%`;
    };
    // Rounded percentage for the legend labels (the bar above uses the raw pct()).
    const pctLabel = (value) => {
      const total = breakdown.value.total || 0;
      if (!total) return '0%';
      return `${Math.round((Number(value || 0) / total) * 100)}%`;
    };

    // --- User ---
    const displayName = computed(() => auth0.user.value?.name || auth0.user.value?.nickname || 'Account');
    const initials = computed(() => {
      const n = (auth0.user.value?.name || auth0.user.value?.email || '?').trim();
      const parts = n.split(/[\s@.]+/).filter(Boolean);
      const first = parts[0]?.[0] || '?';
      const second = parts[1]?.[0] || '';
      return (first + second).toUpperCase();
    });
    const roleLabel = computed(() => {
      if (can(Permissions.DualloAdminView)) return 'Duallo admin';
      if (can(Permissions.OrganizationAdminView)) return 'Administrator';
      return 'Member';
    });

    // Admin is now split: org admins get the org Admin page; Duallo staff get their own Duallo admin
    // page. Personal Models + Profile moved to /profile (reachable from the user menu), so a plain
    // member never lands on an empty Admin page.
    const canOrgAdmin = computed(() => can(Permissions.OrganizationAdminView));
    const canDualloAdmin = computed(() => can(Permissions.DualloAdminView));

    const isActive = (names) => names.includes(route.name);
    const go = (name) => { if (route.name !== name) router.push({ name }); };

    // --- Logout ---
    const confirmLogoutDialog = ref(false);
    const requestLogout = () => { confirmLogoutDialog.value = true; };
    const confirmLogout = () => {
      confirmLogoutDialog.value = false;
      organizationStore.reset();
      assetStore.reset();
      authStore.reset();
      try { sessionStorage.clear(); } catch { /* best-effort */ }
      auth0.logout({ logoutParams: { returnTo: window.location.origin }, federated: true });
    };
    setUnauthorizedHandler(confirmLogout);

    const openHelp = () => {
      const user = auth0.user;
      const userName = user.value?.name || 'Unknown';
      const userId = user.value?.sub || 'Unknown';
      const organizationId = organizationStore.organizationId || 'Unknown';
      const assetId = assetStore.asset ? assetStore.asset.id : null;
      const diagnostics = [
        `Name: ${userName}`,
        `User ID: ${userId}`,
        `Organization ID: ${organizationId}`,
        assetId ? `Asset ID: ${assetId}` : null,
        `Page: ${window.location.href}`,
        `Timestamp: ${new Date().toLocaleString()}`,
        `Viewport: ${window.innerWidth} x ${window.innerHeight}`,
        `Browser: ${navigator.userAgent}`,
      ].filter(Boolean).join('\n');
      const subject = encodeURIComponent(`Support Request — ${userName}`);
      const body = encodeURIComponent(
        `Hi Duallo Support,\n\nPlease describe your issue below:\n\n\n\n` +
        `────────────────────────\nDiagnostic details (please keep)\n────────────────────────\n${diagnostics}\n`
      );
      window.location.href = `mailto:support@duallo.ai?subject=${subject}&body=${body}`;
    };

    onMounted(() => {
      loadSubscription();
      loadAnomalyBreakdown();
    });

    async function loadSubscription() {
      hasSubscription.value = organizationStore.active;
      if (!hasSubscription.value) { planLoading.value = false; return; }
      try {
        plan.value = await OrganizationService.getCurrentSubscriptionByOrganizationIdAsync(organizationStore.organizationId);
        if (plan.value?.monthlyMinutes) {
          usagePct.value = Math.min(Math.round((plan.value.usedMinutes / plan.value.monthlyMinutes) * 100), 100);
        }
        renewalLabel.value = formatRenewal(plan.value?.subscriptionPeriod);
      } catch {
        /* best-effort — the card just hides */
      } finally {
        planLoading.value = false;
      }
    }

    function formatRenewal(period) {
      if (!period) return null;
      const [year, month] = period.split('-').map(Number);
      if (!year || !month) return null;
      const d = new Date(Date.UTC(year, month, 1));
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', timeZone: 'UTC' });
    }

    function formatNumber(num) {
      if (num == null) return '0';
      return Number(num).toLocaleString();
    }

    return {
      Permissions, can,
      organizationLogoUrl, dualloMark, onLogoError,
      hasSubscription, plan, usagePct, usageColor, renewalLabel, planLoading, breakdownLoading,
      breakdown, pct, pctLabel,
      displayName, initials, roleLabel, canOrgAdmin, canDualloAdmin,
      isActive, go,
      confirmLogoutDialog, requestLogout, confirmLogout, openHelp,
      formatNumber,
      uploads, activeUploadCount, retryUpload, removeUpload, pulsing,
    };
  }
};
</script>

<style scoped>
.side-nav {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 14px 10px 12px;
  gap: 8px;
  box-sizing: border-box;
  color: #fff;
}

.nav-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 2px 2px 8px 6px;
}

.brand-link {
  display: flex;
  align-items: center;
  min-width: 0;
  text-decoration: none;
}

.brand-logo { height: clamp(26px, calc(10px + 1.25vw), 34px); max-width: 100%; display: block; }
.brand-mark { display: none; width: 34px; height: 34px; }

.collapse-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* p5-4: >=44px touch target (icon stays visually 18px, centered by flex) */
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border-radius: 8px;
  background: none;
  border: 1px solid transparent;
  color: #8aa0aa;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.collapse-btn:hover { background: rgba(255, 255, 255, 0.06); color: #fff; border-color: rgba(255, 255, 255, 0.08); }

/* Small "has pending" indicator dot on the Findings icon — shown only in the collapsed rail
   (the numeric pill is hidden there). */
.nav-dot { display: none; }

.nav-items {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: clamp(8px, calc(4px + 0.31vw), 10px) clamp(10px, calc(6px + 0.31vw), 12px);
  border: 1px solid transparent;
  border-radius: 9px;
  background: none;
  color: #b7c5cc;
  font: inherit;
  font-size: clamp(0.84rem, calc(0.68rem + 0.2vw), 0.92rem);
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.nav-item:hover { background: rgba(255, 255, 255, 0.05); color: #fff; }

.nav-item.active {
  background: rgba(102, 199, 180, 0.12);
  border-color: rgba(102, 199, 180, 0.4);
  color: var(--duallo-accent-muted);
}

.nav-label { flex: 1 1 auto; min-width: 0; }

.nav-badge {
  flex: 0 0 auto;
  min-width: 22px;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(240, 166, 74, 0.2);
  border: 1px solid rgba(240, 166, 74, 0.45);
  color: #f0b968;
  font-size: 0.72rem;
  font-weight: 700;
  text-align: center;
}

.nav-spacer { flex: 1 1 auto; }

/* Anomaly summary card */
.anomaly-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 11px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.anomaly-head { display: flex; align-items: baseline; gap: 6px; }

.anomaly-total {
  font-size: clamp(1.2rem, calc(12.8px + 0.5vw), 1.4rem);
  font-weight: 800;
  color: var(--duallo-accent-muted);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.anomaly-caption { font-size: 0.74rem; font-weight: 600; color: #cfd8dc; }

.anomaly-bar {
  display: flex;
  height: 5px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
}

.seg { height: 100%; }
.seg-accepted { background: #4ade80; }
.seg-rejected { background: #ef4444; }
.seg-pending { background: #f5a623; }

.anomaly-legend { display: flex; flex-direction: column; gap: 4px; }

.legend {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  color: #a9b8be;
  font-weight: 500;
}
.legend-pct { margin-left: auto; color: #8aa0aa; font-weight: 700; font-variant-numeric: tabular-nums; }

.dot { width: 7px; height: 7px; border-radius: 50%; flex: 0 0 auto; }
.dot-accepted { background: #4ade80; }
.dot-rejected { background: #ef4444; }
.dot-pending { background: #f5a623; }

/* Active-uploads card */
.upload-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(100, 255, 218, 0.18);
  border-radius: 11px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.upload-head { display: flex; align-items: center; gap: 6px; }
.upload-head-title { font-size: 0.74rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #cfd8dc; }
.upload-head-count {
  margin-left: auto; min-width: 20px; padding: 0 6px; height: 18px; border-radius: 999px;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(100, 255, 218, 0.14); border: 1px solid rgba(100, 255, 218, 0.3);
  color: var(--duallo-accent); font-size: 0.7rem; font-weight: 700;
}
.upload-list { display: flex; flex-direction: column; gap: 9px; max-height: 190px; overflow-y: auto; scrollbar-width: thin; }
.upload-row { display: flex; flex-direction: column; gap: 5px; }
.upload-row-top { display: flex; align-items: center; gap: 8px; }
.upload-name {
  flex: 1 1 auto; min-width: 0; font-size: 0.78rem; font-weight: 600; color: #e6edf0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.upload-state { flex: 0 0 auto; display: inline-flex; align-items: center; font-size: 0.72rem; font-weight: 700; color: var(--duallo-accent-muted); font-variant-numeric: tabular-nums; }
.upload-queued { color: #8aa0aa; font-weight: 600; }
.upload-retry {
  display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px;
  border-radius: 6px; background: rgba(248, 113, 113, 0.14); border: 1px solid rgba(248, 113, 113, 0.4);
  color: #ff9c9c; cursor: pointer;
}
.upload-retry:hover { background: rgba(248, 113, 113, 0.24); }
.upload-bar { height: 4px; border-radius: 999px; background: rgba(255, 255, 255, 0.08); overflow: hidden; }
.upload-bar-fill { height: 100%; border-radius: 999px; background: var(--duallo-accent); transition: width 0.25s ease; }
.upload-bar-fill.is-success { background: #4ade80; }
.upload-bar-fill.is-error { background: #f87171; }
.upload-bar-fill.is-queued { background: #6a7d85; }
.upload-err { display: flex; align-items: center; gap: 6px; }
.upload-err-text { flex: 1 1 auto; min-width: 0; font-size: 0.68rem; color: #ff9c9c; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.upload-dismiss { flex: 0 0 auto; background: none; border: none; padding: 0; font: inherit; font-size: 0.68rem; font-weight: 600; color: #8aa0aa; cursor: pointer; text-decoration: underline; }
.upload-dismiss:hover { color: #cfd8dc; }

/* Hand-off flash — draws the eye to the widget when a wizard drops an upload here. */
.upload-card.pulsing, .upload-rail.pulsing { animation: upload-pulse 0.5s ease-in-out 3; }
@keyframes upload-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(100, 255, 218, 0); border-color: rgba(100, 255, 218, 0.18); }
  50% { box-shadow: 0 0 0 4px rgba(100, 255, 218, 0.35); border-color: rgba(100, 255, 218, 0.7); }
}
@media (prefers-reduced-motion: reduce) {
  .upload-card.pulsing, .upload-rail.pulsing { animation: none; }
}

/* Collapsed rail upload indicator */
.upload-rail {
  position: relative; align-self: center; width: 44px; height: 40px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(100, 255, 218, 0.1); border: 1px solid rgba(100, 255, 218, 0.28);
}
.upload-rail-badge {
  position: absolute; top: 2px; right: 4px; min-width: 15px; height: 15px; padding: 0 3px;
  display: inline-flex; align-items: center; justify-content: center; border-radius: 999px;
  background: var(--duallo-accent); color: #04211c; font-size: 0.62rem; font-weight: 800; line-height: 1;
}

/* Plan card */
.plan-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 11px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.plan-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.plan-name {
  font-size: 0.85rem;
  font-weight: 700;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #4ade80;
  flex: 0 0 auto;
}

.status-dot {
  width: 5px; height: 5px; border-radius: 50%;
  background: #4ade80; box-shadow: 0 0 4px rgba(74, 222, 128, 0.7);
}

.plan-bar { border-radius: 999px; }

.plan-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 0.72rem;
  color: #8aa0aa;
  font-variant-numeric: tabular-nums;
}

/* Loading shimmer for the anomaly + plan cards */
.sk {
  position: relative;
  overflow: hidden;
  display: block;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 6px;
}
.sk::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
  animation: sk-shimmer 1.25s ease-in-out infinite;
}
@keyframes sk-shimmer { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) { .sk::after { animation: none; } }

.sk-total { width: 92px; height: 1.2rem; }
.sk-bar { width: 100%; height: 5px; border-radius: 999px; }
.sk-legend-line { width: 100%; height: 0.72rem; }
.sk-plan-name { width: 70px; height: 0.85rem; }
.sk-plan-status { width: 44px; height: 0.68rem; margin-left: auto; }
.sk-plan-bar { width: 100%; height: 4px; border-radius: 999px; }
.sk-plan-meta { width: 80%; height: 0.72rem; }

/* User row */
.user-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px;
  border: none;
  border-radius: 10px;
  background: none;
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: background 0.15s ease;
}

.user-row:hover { background: rgba(255, 255, 255, 0.05); }

.user-avatar {
  flex: 0 0 auto;
  width: clamp(30px, calc(22px + 0.625vw), 34px); height: clamp(30px, calc(22px + 0.625vw), 34px);
  border-radius: 50%;
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 0.78rem; font-weight: 700;
}

.user-text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }

.user-name {
  font-size: 0.85rem; font-weight: 700; color: #fff;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.user-role { font-size: 0.72rem; color: #8aa0aa; }

.user-menu {
  background: #1f2c34;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

/* ── Collapsed icon rail ── */
.side-nav.collapsed { padding-left: 8px; padding-right: 8px; }
.side-nav.collapsed .nav-top { flex-direction: column; align-items: center; gap: 8px; padding: 2px 0 8px; }
.side-nav.collapsed .brand-link { justify-content: center; width: 100%; }
.side-nav.collapsed .brand-logo { display: none; }
.side-nav.collapsed .brand-mark { display: block; }
.side-nav.collapsed .collapse-btn { min-width: 44px; min-height: 44px; }

.side-nav.collapsed .nav-item { position: relative; justify-content: center; gap: 0; padding: 11px 0; }
.side-nav.collapsed .nav-label,
.side-nav.collapsed .nav-badge { display: none; }
.side-nav.collapsed .nav-dot {
  display: block;
  position: absolute;
  top: 8px;
  right: 15px;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #f0a623;
  border: 1.5px solid #141a1f;
}

.side-nav.collapsed .user-row { justify-content: center; padding: 8px 0; }
.side-nav.collapsed .user-text,
.side-nav.collapsed .user-caret { display: none; }

/* Logout dialog (mirrors NavBar) */
.logout-dialog {
  border-radius: 16px !important;
  background: var(--duallo-card-gradient) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5) !important;
  color: #fff;
  padding: 28px 24px 22px;
  text-align: center;
  user-select: none;
}

.logout-icon {
  width: 52px; height: 52px; margin: 0 auto 14px;
  border-radius: 14px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.25);
  display: flex; align-items: center; justify-content: center;
}

.logout-title { font-size: 1.2rem; font-weight: 700; margin: 0 0 6px; color: #fff; }
.logout-text { font-size: 0.92rem; color: #b0bec5; margin: 0 0 22px; line-height: 1.5; }
.logout-actions { display: flex; gap: 10px; justify-content: center; }

.ghost-btn, .danger-btn {
  flex: 1 1 0; max-width: 150px; padding: 10px 18px;
  border-radius: 10px; font: inherit; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.ghost-btn { background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); color: #cfd8dc; }
.ghost-btn:hover { background: rgba(255, 255, 255, 0.08); }
.danger-btn { background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.4); color: #f87171; }
.danger-btn:hover { background: rgba(239, 68, 68, 0.25); }
</style>
